import { lazy, ReactNode, Suspense } from 'react'

import { clsx } from 'clsx'

import {
  Image,
  Notification,
  NotificationVerb,
} from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { iconStyles, SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { MemberAvatarPlaceholder } from '../common/components/member/MemberAvatarPlaceholder.js'
import { dayjs } from '../common/lib/dayjs.js'
import { T } from '../i18n/components/T.js'
import { I18nShape, useI18n } from '../i18n/index.js'

import { NotificationRowActions } from './NotificationRowActions.js'
import { ReactionIcon } from './ReactionIcon.js'

const NotificationContent = lazy(() =>
  import('./NotificationContent.js').then(module => ({
    default: module.NotificationContent,
  })),
)

export const getLink = ({ meta }: Notification) => {
  return meta?.relativeUrl || ''
}

const MAX_TITLE_CONTENT_LENGTH = 80

const truncate = (text: string) => {
  const characters = Array.from(text || '')
  return characters.length > MAX_TITLE_CONTENT_LENGTH
    ? `${characters.slice(0, MAX_TITLE_CONTENT_LENGTH).join('').trimEnd()}…`
    : text
}

const Summary = ({ content }: { content: string }) => {
  if (!content || content.length <= 3) {
    return null
  }

  return (
    <article
      className={clsx(
        'prose p-3 rounded-base my-2 bg-surface-pressed',
        // Sits above the row's stretched link: plain text lets clicks through
        // to the link, while embedded controls and links stay interactive.
        'relative z-10 pointer-events-none',
        '[&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_iframe]:pointer-events-auto [&_video]:pointer-events-auto [&_audio]:pointer-events-auto',
        '[&_label]:pointer-events-auto [&_input]:pointer-events-auto',
        // Image lightbox triggers and attachment rows handle their own clicks.
        '[&_[data-slide-index]]:pointer-events-auto',
        // Wide tables scroll inside HtmlContent's overflow wrapper.
        '[&_.overflow-x-auto]:pointer-events-auto',
      )}
    >
      <Suspense fallback={<></>}>
        <NotificationContent value={content} />
      </Suspense>
    </article>
  )
}

const Highlight = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-content-subdued">{children}</strong>
)

const getSummary = ({
  notification: { verb, meta, target },
  intl: { $t },
}: {
  notification: Notification
  intl: I18nShape
}) => {
  const UNAVAILABLE_POST = $t({
    defaultMessage: 'Unavailable Post',
    id: 'Notification.Types.UnavailablePost',
  })

  switch (verb) {
    case NotificationVerb.COMMENT_CREATED:
    case NotificationVerb.POST_CREATED:
    case NotificationVerb.POST_UPDATED:
      return <Summary content={meta.body} />

    case NotificationVerb.MEMBER_MENTIONED:
      return <Summary content={target?.summary || UNAVAILABLE_POST} />

    default:
      return null
  }
}

const getTitle = ({
  notification: { actor, target, verb, meta, summary },
  intl: { $t },
}: {
  notification: Notification
  intl: I18nShape
}) => {
  const UNAVAILABLE_MEMBER = $t({
    defaultMessage: 'Unavailable Member',
    id: 'Notification.Types.UnavailableMember',
  })
  const UNAVAILABLE_SPACE = $t({
    defaultMessage: 'Unavailable Space',
    id: 'Notification.Types.UnavailableSpace',
  })
  const UNAVAILABLE_POST = $t({
    defaultMessage: 'Unavailable Post',
    id: 'Notification.Types.UnavailablePost',
  })

  const actorName = (
    <Highlight>{actor?.summary || UNAVAILABLE_MEMBER}</Highlight>
  )
  const postTitle = <Highlight>{meta.textTitle || UNAVAILABLE_POST}</Highlight>
  switch (verb) {
    case NotificationVerb.COMMENT_CREATED:
      return (
        <div>
          <T
            id="Notification.Types.Replied.Title"
            defaultMessage="{actor} replied to {content}"
            values={{ actor: actorName, content: postTitle }}
          />
        </div>
      )

    case NotificationVerb.COMMENT_DELETED:
      return (
        <T
          id="Notification.Types.DeletedReply.Title"
          defaultMessage="{content} has been deleted"
          values={{ content: postTitle }}
        />
      )

    case NotificationVerb.COMMENT_UPDATED:
      return (
        <T
          id="Notification.Types.ReplyUpdated.Title"
          defaultMessage="{content} has been updated"
          values={{ content: postTitle }}
        />
      )

    case NotificationVerb.REACTION_CREATED:
      return (
        <T
          id="Notification.Types.Reacted.Title"
          defaultMessage="{actor} reacted to {has_title, select, true {{content}} other {your post}}"
          values={{
            actor: actorName,
            has_title: !!(target?.name || meta.textBody),
            content: (
              <Highlight>{truncate(target?.name || meta.textBody)}</Highlight>
            ),
          }}
        />
      )

    case NotificationVerb.POST_CREATED:
      return (
        <div>
          <T
            id="Notification.Types.Posted.Title"
            defaultMessage="{actor} posted {content} in {target}"
            values={{
              actor: actorName,
              content: <Highlight>{postTitle}</Highlight>,
              target: (
                <Highlight>{target?.summary || UNAVAILABLE_SPACE}</Highlight>
              ),
            }}
          />
        </div>
      )

    case NotificationVerb.POST_DELETED:
      return (
        <div>
          <T
            id="Notification.Types.DeletedPost.Title"
            defaultMessage="{content} has been deleted from {target}"
            values={{
              content: postTitle,
              target: (
                <Highlight>{target?.summary || UNAVAILABLE_SPACE}</Highlight>
              ),
            }}
          />
        </div>
      )

    case NotificationVerb.POST_UPDATED:
      return (
        <div>
          <T
            id="Notification.Types.UpdatedPost.Title"
            defaultMessage="{content} has been updated in {target}"
            values={{
              content: postTitle,
              target: (
                <Highlight>{target?.summary || UNAVAILABLE_SPACE}</Highlight>
              ),
            }}
          />
        </div>
      )

    case NotificationVerb.MEMBER_MENTIONED:
      return (
        <div>
          <T
            id="Notification.Types.MentionedYou.Title"
            defaultMessage="{actor} mentioned you in {content}"
            values={{
              actor: actorName,
              content: postTitle,
            }}
          />
        </div>
      )

    case NotificationVerb.JOIN_REQUEST_STATUS_UPDATED: {
      switch (summary) {
        case 'APPROVED':
          return (
            <T
              id="Notification.Types.SpaceJoinRequestApproved.Title"
              defaultMessage="Your request to join {target} has been approved"
              values={{
                target: (
                  <Highlight>{target?.summary || UNAVAILABLE_SPACE}</Highlight>
                ),
              }}
            />
          )

        case 'DECLINED':
          return (
            <T
              id="Notification.Types.SpaceJoinRequestRejected.Title"
              defaultMessage="Your request to join {target} has been rejected"
              values={{
                target: (
                  <Highlight>{target?.summary || UNAVAILABLE_SPACE}</Highlight>
                ),
              }}
            />
          )

        default:
          return null
      }
    }

    case NotificationVerb.EVENT_DELETED:
      return (
        <T
          id="Notification.Types.EventDeleted.Title"
          defaultMessage="{content} has been deleted"
          values={{ content: postTitle }}
        />
      )

    case NotificationVerb.EVENT_REGISTRATION_CREATED:
      return (
        <T
          id="Notification.Types.EventRegistrationCreated.Title"
          defaultMessage="You have been registered for {content}"
          values={{ content: postTitle }}
        />
      )

    case NotificationVerb.EVENT_UPDATED:
      return (
        <T
          id="Notification.Types.EventUpdated.Title"
          defaultMessage="{content} has been updated"
          values={{ content: postTitle }}
        />
      )
    case NotificationVerb.EVENT_CREATED:
      return (
        <T
          id="Notification.Types.EventCreated.Title"
          defaultMessage="{content} has been created"
          values={{ content: postTitle }}
        />
      )

    case NotificationVerb.EVENT_REGISTRATION_CANCELED:
      return (
        <T
          id="Notification.Types.EventRegistrationCanceled.Title"
          defaultMessage="Your registration for {content} has been canceled"
          values={{ content: postTitle }}
        />
      )
    case NotificationVerb.EVENT_REMINDER_CREATED:
      return (
        <T
          id="Notification.Types.EventReminder.Title"
          defaultMessage="{content} starts soon"
          values={{ content: <Highlight>{postTitle}</Highlight> }}
        />
      )
    case NotificationVerb.MESSAGE_CREATED:
      return null

    default: {
      const _exhaustiveCheck: never = verb
      return _exhaustiveCheck
    }
  }
}

export const NotificationRow = ({
  notification,
  showSummary = true,
  href,
  onLinkClick,
}: {
  notification: Notification
  showSummary?: boolean
  href?: string
  onLinkClick?: () => void
}) => {
  const intl = useI18n()
  const { Link } = useRouter()

  if (!notification?.meta) {
    return null
  }

  const title = getTitle({ notification, intl })
  const timestamp = dayjs(notification.createdAt).fromNow()
  const withLink = (content: ReactNode, ariaLabel?: string) =>
    href ? (
      <Link
        href={href}
        onClick={onLinkClick}
        aria-label={ariaLabel}
        className="block hover:underline after:absolute after:inset-0 after:content-['']"
      >
        {content}
      </Link>
    ) : (
      content
    )

  const hasActor = !!notification.actor
  const actorAvatarUrl = (notification.actor?.media as Image)?.urls?.thumb
  const isReaction =
    notification.object?.id &&
    notification.verb === NotificationVerb.REACTION_CREATED
  return (
    <div className="flex gap-3 w-full">
      <div className="shrink-0">
        {hasActor ? (
          <Avatar
            icon={
              isReaction ? (
                <ReactionIcon
                  reactionKey={notification.object?.id}
                  postTypeId={notification.target?.post?.postTypeId}
                />
              ) : null
            }
            name={notification.actor?.summary}
            src={actorAvatarUrl}
            fallback={MemberAvatarPlaceholder}
          />
        ) : (
          <SvgIcon
            className={clsx('relative', iconStyles({ size: '2.5x' }))}
            name="info-circle"
          />
        )}
      </div>
      <div className="notification-row grow min-w-0 space-y-1">
        <div>
          <div className="text-sm text-content-subdued">
            {title ? withLink(title) : null}
          </div>
          <div className="text-sm text-content-subdued flex-shrink-0">
            {title
              ? timestamp
              : withLink(
                  timestamp,
                  intl.$t(
                    {
                      defaultMessage: 'View notification, {timestamp}',
                      id: 'Notification.ViewNotification',
                    },
                    { timestamp },
                  ),
                )}
          </div>
          {showSummary && getSummary({ notification, intl })}
          <NotificationRowActions notification={notification} />
        </div>
      </div>
      <div className="w-2">
        {!notification.read ? (
          <div className="bg-action-primary rounded-full w-2 h-2" />
        ) : null}
      </div>
    </div>
  )
}
